import * as THREE from 'three'
import { SectionFrame } from './types'
import { frameCameraPose, safeCameraPose } from './frameMath'

/**
 * Pointing a camera at a section, once, for both apps.
 *
 * SHARED WITH THE CUSTOMIZER. Depends on `three` and nothing else — the controls are taken
 * structurally rather than imported, so this stays on the right side of that rule while
 * still owning every quirk of driving them.
 *
 * WHY THIS IS NOT JUST setLookAt
 * ------------------------------
 * camera-controls applies a pose like this, in its update():
 *
 *     this._camera.position.setFromSpherical(...).add(this._target)   // parent-LOCAL
 *     this._camera.lookAt(this._target)                               // WORLD
 *
 * It writes the position as parent-relative but aims with Object3D.lookAt, which reads the
 * target AND the camera's own position in world space. Those agree only while the camera's
 * parent is identity — and both apps parent the camera to a scene they keep rotated, to
 * give the turntable feel. So the EYE must be handed over in the camera's parent space and
 * the TARGET in world. Passing both in either space puts the camera in the right place
 * aimed at the wrong point.
 *
 * That mismatch is worth describing precisely, because it hides well. Every value a caller
 * logs looks perfect — the pose really was computed correctly — and the error only shows in
 * the rendered image. In the admin portal it put the section at NDC x 2.2, clean off the
 * screen. In the Customizer, whose scene rotation chases the camera and so settles to a
 * fixed point, it showed up as the FIRST section framing after a manual rotate being wrong
 * and every one after it being right, which is a much easier thing to dismiss as a glitch.
 *
 * Two further things the controls need and will not do for themselves:
 *
 *   - A FOCAL OFFSET survives setLookAt. Pan the view once and every later framing arrives
 *     slid sideways by that much, for the rest of the session.
 *   - The DOLLY FLOOR is configured for whole products. A section is a few centimetres
 *     across and wants the camera far closer than a product-sized floor allows, so the
 *     floor silently overrides the distance asked for.
 */

/**
 * How close a shopper may get to a section, as a fraction of the distance that frames it.
 *
 * 0.12 is roughly eight times the area of "the section fills the view" — close enough to
 * place a small detail by eye on a 25 mm patch, which is the size sections on a pistol or a
 * pen actually are. The framing distance alone is nowhere near enough for that, and it is
 * worst on a phone, where the artwork is already small on the screen before you start.
 */
export const SECTION_DOLLY_FLOOR = 0.12

/**
 * ...but never closer than this, in metres, however small the section is.
 *
 * A fraction alone has no lower bound: a 5 mm section would invite the camera to within a
 * few millimetres of the surface, through the near plane at 0.01 and usually through the
 * product itself on anything that is not flat.
 */
export const SECTION_MIN_DISTANCE = 0.03

/** The parts of an orbit controller this needs. Structural, so nothing is imported. */
export interface OrbitControlsLike {
  minPolarAngle: number
  maxPolarAngle: number
  azimuthAngle: number
  minDistance: number
  maxDistance: number
  setFocalOffset(x: number, y: number, z: number, enableTransition?: boolean): void
  setLookAt(
    eyeX: number,
    eyeY: number,
    eyeZ: number,
    targetX: number,
    targetY: number,
    targetZ: number,
    enableTransition?: boolean
  ): Promise<void> | void
  update(delta: number): boolean
}

export interface AimOptions {
  /** Animate, or snap. Snap for a snapshot: an animated move has to be waited out. */
  transition?: boolean
  /**
   * Let the camera come this fraction of the framing distance closer than the controls
   * would otherwise allow. Null leaves the dolly range alone.
   */
  dollyFloorFactor?: number | null
  /** Floor for the above, in metres, so a tiny section cannot invite the camera inside it. */
  minDistanceAbsolute?: number
  /** Clear any pan first. On by default; see the header. */
  clearPan?: boolean
}

/** Where to aim, in world space, and how far back to sit. */
export interface AimTarget {
  centre: THREE.Vector3
  normal: THREE.Vector3
  distance: number
}

/**
 * Where to put a camera to frame a section, as world-space values.
 *
 * Separate from the aiming so a caller can retarget — the Customizer centres on an element
 * INSIDE the section while keeping the section's framing distance.
 */
export function frameAimTarget(
  frame: SectionFrame,
  productRoot: THREE.Object3D,
  fovDeg: number,
  aspect = 1
): AimTarget {
  const { eye, target } = frameCameraPose(frame, productRoot, fovDeg, undefined, aspect)
  return { centre: target, normal: eye.clone().sub(target).normalize(), distance: eye.distanceTo(target) }
}

/** Aim the camera, handling everything in this file's header. Returns false if it cannot. */
export function aimCameraAt(
  aim: AimTarget,
  camera: THREE.PerspectiveCamera,
  controls: OrbitControlsLike,
  options: AimOptions = {}
): Promise<void> | void {
  const { centre, normal, distance } = aim

  if (options.clearPan !== false) controls.setFocalOffset(0, 0, 0, false)

  if (options.dollyFloorFactor != null) {
    const floor = Math.max(distance * options.dollyFloorFactor, options.minDistanceAbsolute ?? 0)
    if (controls.minDistance > floor) controls.minDistance = floor
  }

  const parent = camera.parent
  let toCameraParent: THREE.Matrix4 | null = null
  if (parent) {
    parent.updateMatrixWorld(true)
    toCameraParent = new THREE.Matrix4().copy(parent.matrixWorld).invert()
  }

  const pose = safeCameraPose(centre, normal, distance, {
    toCameraParent,
    minPolar: controls.minPolarAngle,
    maxPolar: controls.maxPolarAngle,
    fallbackAzimuth: controls.azimuthAngle,
  })

  // EYE parent-local, TARGET world. Not a slip — see the header.
  const moved = controls.setLookAt(
    pose.eye.x,
    pose.eye.y,
    pose.eye.z,
    centre.x,
    centre.y,
    centre.z,
    options.transition !== false
  )
  if (options.transition === false) controls.update(0)
  return moved
}
